import { useEffect, useState } from 'react'
import { ImageOff } from 'lucide-react'
import softStar from '@/assets/soft-star.svg'
import { cn, Skeleton } from '@teach/ui'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  url: string | null | undefined
  /** Крупный вид — в попапе подробностей, мелкий — в строке списка. */
  size: 'row' | 'detail'
}

/**
 * Картинка прогона в истории движений — и то, что показывается вместо неё.
 *
 * Мёртвая ссылка тут не редкость, а ШТАТНОЕ состояние: студия сносит блобы из
 * CDN, не проверяя, кто на них ссылается, а ссылка в леджере живёт вечно —
 * запись о списании неизменна по построению. Браузер на такой `src` рисует
 * пустую рамку, и строка выглядит поломкой интерфейса вместо истории платежа.
 *
 * Два пустых состояния РАЗНЫЕ, и путать их нельзя:
 * «картинки не было» — прогон, который картинок не рисует (генерация курса,
 * правка блоков, перевод); «картинка была и пропала» — удалённый блоб. Иконка
 * «нет картинки» над первым соврала бы, что чего-то не хватает.
 */
export const HistoryThumb = ({ url, size }: Props) => {
  const m = useMessages(messages)
  const [failed, setFailed] = useState(false)
  const [loaded, setLoaded] = useState(false)
  // Ссылка меняется при листании страниц — отметки о сбое и загрузке надо
  // снимать вместе с ней, иначе следующая картинка в переиспользованном узле
  // не покажется (или покажется без скелета).
  useEffect(() => {
    setFailed(false)
    setLoaded(false)
  }, [url])

  const box = cn('shrink-0 rounded-lg', size === 'row' ? 'size-11 rounded-lg' : 'max-h-64 w-full')

  if (url && !failed)
    return (
      // Пока картинка грузится — мигающий квадрат на её месте, потом она
      // проявляется: иначе строки сначала стоят с пустой дырой слева.
      <div className={cn('relative', size === 'row' ? box : 'w-full')}>
        {loaded ? null : (
          <Skeleton className={cn('absolute inset-0', size === 'row' ? 'rounded-lg' : 'h-40')} />
        )}
        <img
          src={url}
          alt=""
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          className={cn(
            box,
            size === 'row' ? 'object-cover' : 'object-contain',
            'transition-opacity duration-200',
            loaded ? 'opacity-100' : 'opacity-0',
            // До загрузки у крупной картинки нет высоты — держим место под скелет.
            size === 'detail' && !loaded ? 'h-40' : null,
          )}
        />
      </div>
    )

  const iconSize = size === 'row' ? 'size-4' : 'size-8'
  return (
    <div
      // Подпись только у пропавшей: у прогона без картинок объяснять нечего.
      title={url ? m.imageGone : undefined}
      className={cn(
        box,
        'flex items-center justify-center bg-muted text-muted-foreground',
        size === 'detail' ? 'h-32' : null,
      )}
    >
      {url ? (
        <ImageOff className={iconSize} />
      ) : (
        // Наша звёздочка (как у вкладки «Использование»), серым силуэтом:
        // маска + токен цвета, а не перекрашенный файл.
        <span
          aria-hidden="true"
          className={cn('bg-muted-foreground', iconSize)}
          style={{
            maskImage: `url("${softStar}")`,
            WebkitMaskImage: `url("${softStar}")`,
            maskSize: 'contain',
            WebkitMaskSize: 'contain',
            maskRepeat: 'no-repeat',
            WebkitMaskRepeat: 'no-repeat',
          }}
        />
      )}
    </div>
  )
}
